import moment from "moment";

export const MIN_SEND_DAY = 1;
export const MAX_SEND_DAY = 10;

/**
 * Upper bound on the day-by-day range scan. The End Date input accepts typed
 * dates, so without a cap a far-future year would make getEligibleSendDates
 * iterate millions of days on every render.
 */
export const MAX_RANGE_SCAN_DAYS = 366;

/**
 * Splits one CSV line into cells, honoring double-quoted cells so commas
 * inside quotes (e.g. `"Doe, John"`) don't shift columns. `""` inside a
 * quoted cell is an escaped quote.
 */
export function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      cells.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells;
}

export interface ParsedRecipientsCsv {
  userIds: string[];
  /** Whether the CSV header contains a "Send Date" column */
  hasSendDateColumn: boolean;
  /** Valid day values (1-10) keyed by user ID */
  dayByUserId: Record<string, number>;
  /** User IDs whose Send Date value was blank or invalid (column present) */
  invalidDayUserIds: string[];
}

/**
 * Parses a recipients CSV. The first column holds user IDs (a header row is
 * required). An optional "Send Date" column (case/spacing-insensitive header
 * match) holds day values from 1 to MAX_SEND_DAY.
 *
 * Returns null when the CSV has no data rows. A CSV with data rows but no
 * usable user IDs returns an object with an empty userIds array so the
 * caller can show a precise error.
 */
export function parseRecipientsCsv(text: string): ParsedRecipientsCsv | null {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (lines.length <= 1) {
    return null;
  }

  const header = splitCsvLine(lines[0]);
  const sendDateColumnIndex = header.findIndex(
    (h) =>
      h
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, "") === "senddate",
  );
  const hasSendDateColumn = sendDateColumnIndex !== -1;

  const userIds: string[] = [];
  const dayByUserId: Record<string, number> = {};
  const invalidDayUserIds: string[] = [];

  for (const line of lines.slice(1)) {
    const columns = splitCsvLine(line);
    const userId = columns[0].trim();
    if (!userId) {
      continue;
    }
    userIds.push(userId);

    if (hasSendDateColumn) {
      const rawDay = (columns[sendDateColumnIndex] || "").trim();
      const day = /^\d+$/.test(rawDay) ? parseInt(rawDay, 10) : NaN;
      if (day >= MIN_SEND_DAY && day <= MAX_SEND_DAY) {
        dayByUserId[userId] = day;
      } else {
        invalidDayUserIds.push(userId);
      }
    }
  }

  return { userIds, hasSendDateColumn, dayByUserId, invalidDayUserIds };
}

/**
 * Returns the weekdays (Mon-Fri) between start and end inclusive, in order.
 * Day N in the CSV Send Date column maps to the Nth entry of this list.
 */
export function getEligibleSendDates(
  start: moment.Moment,
  end: moment.Moment,
): moment.Moment[] {
  const dates: moment.Moment[] = [];
  const current = start.clone().startOf("day");
  const last = end.clone().startOf("day");
  let scannedDays = 0;
  while (
    current.isSameOrBefore(last, "day") &&
    scannedDays < MAX_RANGE_SCAN_DAYS
  ) {
    if (!isWeekend(current)) {
      dates.push(current.clone());
    }
    current.add(1, "day");
    scannedDays++;
  }
  return dates;
}

export function isWeekend(date: moment.Moment): boolean {
  return date.day() === 0 || date.day() === 6;
}

export interface ScheduleRow {
  day: number;
  date: moment.Moment;
  userCount: number;
}

export interface SchedulePreview {
  rows: ScheduleRow[];
  /** Users without a valid Send Date value, defaulted to Day 1 */
  defaultedUserCount: number;
}

/**
 * Maps each user's Send Date day to a concrete date within the eligible
 * dates and aggregates users per day. Users without a valid day value
 * default to Day 1.
 */
export function buildSchedulePreview(
  userIds: string[],
  dayByUserId: Record<string, number>,
  eligibleDates: moment.Moment[],
): { preview?: SchedulePreview; error?: string } {
  if (eligibleDates.length === 0) {
    return {
      error: "The selected date range does not contain any weekdays.",
    };
  }

  const countByDay = new Map<number, number>();
  let defaultedUserCount = 0;
  for (const userId of userIds) {
    let day = dayByUserId[userId];
    if (!day) {
      day = MIN_SEND_DAY;
      defaultedUserCount++;
    }
    countByDay.set(day, (countByDay.get(day) || 0) + 1);
  }

  const daysUsed = Array.from(countByDay.keys()).sort((a, b) => a - b);
  const maxDay = daysUsed[daysUsed.length - 1];
  if (maxDay > eligibleDates.length) {
    return {
      error:
        `The CSV uses Send Date day ${maxDay}, but the selected range only ` +
        `contains ${eligibleDates.length} weekday(s). Extend the end date.`,
    };
  }

  const rows = daysUsed.map((day) => ({
    day,
    date: eligibleDates[day - 1].clone(),
    userCount: countByDay.get(day),
  }));

  return { preview: { rows, defaultedUserCount } };
}

export function chunkArray<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}
